import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { Table } from 'antd';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useModals } from '@/components/modals/ModalProvider';
import { getPurchaseOrderDetail } from '@/libs/fleet-api/services/purchase-order';

export type PurchaseOrderDetailModalState = null | {
	mppId: number;
	purchaseOrderId: number;
	mklNo?: string | null;
	spfNo?: string | null;
};

export type PurchaseOrderDetailModalProps = {
	modal: PurchaseOrderDetailModalState;
	onClose: () => void;
};

const formatDate = (value: string | Date | undefined | null) =>
	value ? dayjs(value).format('DD-MM-YYYY') : '-';

const PurchaseOrderDetailModal: FC<PurchaseOrderDetailModalProps> = ({
	modal,
	onClose,
}) => {
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const { data, isLoading } = useQuery({
		queryKey: ['getPurchaseOrderDetailModal', modal?.mppId] as const,
		queryFn: ({ queryKey }) => {
			const [, mppId] = queryKey;
			return getPurchaseOrderDetail({
				pagination: { pageNumber: 1, pageSize: 100 },
				mppId: mppId!,
			}).then((res) => res.data);
		},
		enabled: !!modal?.mppId,
	});

	const detail = data?.details?.find(
		(item) => item.purchaseOrderId === modal?.purchaseOrderId,
	);

	const tableData = detail
		? [
				{
					key: 1,
					no: 1,
					noMkl: modal?.mklNo || '-',
					noSpf: modal?.spfNo || '-',
					noMpp: data?.mppNo || '-',
					noPo: detail.purchaseOrderNo || '-',
					applicationId: detail.applicationId || '-',
					noAgreement: detail.agreementNo || '-',
					supplier: detail.supplierName || '-',
					merkAsset: detail.brand || '-',
					ntf: detail.ntfAmount,
					tglTerbitPo: detail.purchaseOrderReleaseDate,
					tglPoExpired: detail.purchaseOrderExpiredDate,
					tglPerpanjanganPo: detail.purchaseOrderExtendDate,
					status: detail.status || '-',
				},
			]
		: [];

	return (
		<FleetModal
			open={Boolean(modal)}
			width={1100}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{ hidden: true }}
			onCancel={() => onClose?.()}
		>
			<FleetTitle>Detail Purchase Order</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{ marginBottom: 20 }}
				items={[
					[
						[
							'Nama Customer',
							data?.customerName ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										if (data.customerId) openCustomerDetail(data.customerId);
									}}
								>
									{data.customerName}
								</a>
							) : (
								'-'
							),
						],
						[
							'Group Customer BMPP',
							data?.bmppGroupId ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(data.bmppGroupId);
									}}
								>
									{data.bmppGroupName}
								</a>
							) : (
								'-'
							),
						],
					],
				]}
			/>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={tableData}
				pagination={false}
				rowKey="key"
				columns={[
					{ title: 'No', dataIndex: 'no', align: 'center', width: 1 },
					{ title: 'No. MKL', dataIndex: 'noMkl' },
					{ title: 'No.SPF', dataIndex: 'noSpf' },
					{ title: 'No. MPP', dataIndex: 'noMpp' },
					{ title: 'No. PO', dataIndex: 'noPo' },
					{ title: 'Application ID', dataIndex: 'applicationId' },
					{ title: 'No Agreement', dataIndex: 'noAgreement' },
					{ title: 'Supplier', dataIndex: 'supplier' },
					{ title: 'Merk Asset', dataIndex: 'merkAsset' },
					{ title: 'NTF', dataIndex: 'ntf' },
					{
						title: 'Tanggal Terbit PO',
						dataIndex: 'tglTerbitPo',
						render: (v) => formatDate(v),
					},
					{
						title: 'Tanggal PO Expired',
						dataIndex: 'tglPoExpired',
						render: (v) => formatDate(v),
					},
					{
						title: 'Tanggal Perpanjangan PO',
						dataIndex: 'tglPerpanjanganPo',
						render: (v) => formatDate(v),
					},
					{ title: 'Status', dataIndex: 'status' },
				]}
			/>
		</FleetModal>
	);
};

export default PurchaseOrderDetailModal;

export const usePurchaseOrderDetailModal = () => {
	return useState<PurchaseOrderDetailModalState>(null);
};
